import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { Icons, Box, IconButton, Stack, TextField, Typography } from "@curves/ui-base";
import { formatCurrencyFn, isFunction, refIsRequiredError } from "@util/utils";

const ProductItem = forwardRef(({ item, onUpdatePrice, onUpdateQuantity, onClickDelete, onClickNotes, readOnly = false, showDiscount = false }, ref) => {
	const { name, fixedPrice, price, qty, discountPrice = 0, sku, desc, productType, stock, isLimited, isPriceEditable, allowDelete, allowZeroQuantity } = item || {};

	// 計算小計
	const itemTotal = (price - discountPrice) * qty;

	// 計算數量上限：如果 isLimited 為 true 且 stock 有值，則使用 stock，否則不限制
	const maxQuantity = isLimited && stock !== undefined && stock !== null ? Number(stock) : undefined;
	const hasQuantityLimit = maxQuantity !== undefined;

	// 判斷價格是否可編輯：預設為 true（可編輯），如果明確設定為 false 或唯讀模式則不可編輯
	const priceEditable = !readOnly && isPriceEditable !== false;

	// 判斷是否允許刪除：預設為 true（允許），如果明確設定為 false 則不允許
	const canDelete = allowDelete !== false;

	// 判斷是否允許數量為0：預設為 false（不允許），如果明確設定為 true 則允許
	const canZeroQuantity = allowZeroQuantity === true;

	// 數量輸入框的 min 值
	const minQuantity = canZeroQuantity ? 0 : 1;

	const priceRef = useRef(null);
	const qtyRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			isError: () => refIsRequiredError(priceRef, qtyRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<Box className="order-cart-item">
			<Stack className="order-cart-item-row" direction="row" alignItems="center">
				{/*商品資訊*/}
				<Box className="order-cart-item-col order-cart-item-product">
					<Box className="order-cart-item-product-name">{name}</Box>
					<Box className="order-cart-item-product-sku">{sku}</Box>
					{productType && <Box className="order-cart-item-product-type">類型：{productType}</Box>}
					{fixedPrice && <Box className="order-cart-item-product-price">定價：{formatCurrencyFn(fixedPrice, { symbol: "$" })}</Box>}
					{desc && <Box className="order-cart-item-product-desc">備註：{desc}</Box>}
				</Box>

				{/* 價格 */}
				<Box className="order-cart-item-col order-cart-item-price">
					{readOnly || !priceEditable ? (
						<Typography className="mb-0">{formatCurrencyFn(price, { symbol: "$" })}</Typography>
					) : (
						<TextField
							key={price}
							defaultValue={price}
							ref={priceRef}
							maskType={"MONEY"}
							inputProps={{ placeholder: "請輸入金額", min: 0 }}
							onChangeNative={priceEditable ? onUpdatePrice : undefined}
							required
						/>
					)}
				</Box>

				{/* 折扣金額顯示 */}
				{showDiscount && (
					<Box className="order-cart-item-col order-cart-item-discount">
						<Typography className="discount-text mb-0">{discountPrice ? `-${formatCurrencyFn(discountPrice, { symbol: "$" })}` : ""}</Typography>
					</Box>
				)}

				{/* 數量 */}
				<Box className="order-cart-item-col order-cart-item-qty">
					{readOnly ? (
						<Typography className="mb-0">{qty}</Typography>
					) : (
						<TextField
							key={qty}
							defaultValue={qty}
							ref={qtyRef}
							maskType={"MONEY"}
							inputProps={{
								placeholder: hasQuantityLimit ? `最多 ${maxQuantity}` : canZeroQuantity ? "可為0" : "請輸入數量",
								min: minQuantity,
								max: maxQuantity,
							}}
							onChangeNative={onUpdateQuantity}
							required={!canZeroQuantity}
						/>
					)}
				</Box>

				{/* 小計 */}
				<Box className="order-cart-item-col order-cart-item-total">{formatCurrencyFn(itemTotal, { symbol: "$" })}</Box>

				{/* 操作按鈕 */}
				{!readOnly && (
					<Stack className="order-cart-item-col order-cart-item-actions" direction="row" justifyContent="end" alignItems="center">
						<IconButton className="icon-button-column" onClick={onClickNotes}>
							<Icons.Appointment />
							<Typography className="icon-button-column-text">備註</Typography>
						</IconButton>
						{canDelete && isFunction(onClickDelete) && (
							<IconButton className="icon-button-column" onClick={onClickDelete}>
								<Icons.Cancel />
								<Typography className="icon-button-column-text">刪除</Typography>
							</IconButton>
						)}
					</Stack>
				)}
			</Stack>
		</Box>
	);
});

ProductItem.displayName = "ProductItem";

export default ProductItem;
